import { useEffect, useId, useState } from "react";
import { teamInitials } from "@/utils/format";
import type { Team } from "@/types/match";
import { resolveTeamBadge } from "@/services/logoResolver";

interface Props {
  team: Team;
  size?: "sm" | "md" | "lg";
}

const sizeMap = {
  sm: { box: "w-6 h-6", text: "text-[8px]" },
  md: { box: "w-9 h-9", text: "text-[10px]" },
  // Scales down on narrow screens — used in the match detail header where
  // two of these plus two names/scores need to fit a 375px-wide phone.
  lg: { box: "w-11 h-11 sm:w-16 sm:h-16", text: "text-sm sm:text-lg" },
};

/**
 * Renders a real crest when one's available — either `team.crestUrl`
 * (already-provided data, e.g. from API-Football) or a live lookup against
 * TheSportsDB (see services/logoResolver.ts) when it isn't. Falls back to a
 * generated shield badge in the team's own colors if neither resolves, or
 * if the resolved image URL turns out broken at runtime — never a plain
 * gray placeholder, and never a broken-image icon.
 */
export function TeamCrest({ team, size = "md" }: Props) {
  const { box, text } = sizeMap[size];
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(team.crestUrl);
  const [failed, setFailed] = useState(false);
  // The same team often renders in several places at once (a match card, the
  // header ticker, search results). Using team.id as the gradient's id would
  // put multiple <linearGradient> elements with the *same* id in the DOM —
  // invalid SVG that browsers resolve inconsistently, sometimes dropping the
  // fill entirely. useId() guarantees a unique id per rendered instance.
  const gradientId = `crest-${useId()}`;

  useEffect(() => {
    setFailed(false);
    if (team.crestUrl) {
      setResolvedUrl(team.crestUrl);
      return;
    }
    let cancelled = false;
    resolveTeamBadge(team.name).then((url) => {
      if (!cancelled) setResolvedUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [team.id, team.name, team.crestUrl]);

  if (resolvedUrl && !failed) {
    return (
      <img
        src={resolvedUrl}
        alt={`${team.name} crest`}
        className={`${box} object-contain shrink-0`}
        loading="lazy"
        onError={() => setFailed(true)}
      />
    );
  }

  const initials = teamInitials(team.shortName || team.name);

  return (
    <svg
      viewBox="0 0 48 54"
      className={`${box} shrink-0 drop-shadow-sm`}
      role="img"
      aria-label={`${team.name} crest`}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={team.primaryColor} />
          <stop offset="100%" stopColor={team.secondaryColor} />
        </linearGradient>
      </defs>
      <path
        d="M24 1 L45 8 V25 C45 39 36 48 24 53 C12 48 3 39 3 25 V8 Z"
        fill={`url(#${gradientId})`}
        stroke="rgba(255,255,255,0.18)"
        strokeWidth="1"
      />
      <path
        d="M24 4 L42 10 V25 C42 37 34.5 45 24 49.7 C13.5 45 6 37 6 25 V10 Z"
        fill="none"
        stroke="rgba(255,255,255,0.25)"
        strokeWidth="0.75"
      />
      <text
        x="24"
        y="30"
        textAnchor="middle"
        dominantBaseline="middle"
        className={`font-display font-bold ${text}`}
        fill="rgba(255,255,255,0.95)"
      >
        {initials}
      </text>
    </svg>
  );
}
